'use client';

import { useCanvassers } from '@/containers/Jobs/hooks/useCanvassers';
import { useJobDetails } from '@/containers/Jobs/hooks/useJobDetails';
import { selectAccountDetails } from '@/lib/redux/app.selectors';
import { DatePicker } from '@/shared/components/FormInputs';
import { useInitialsValidation } from '@/shared/hooks/useInitialsValidation';
import { getFormattedDateYYYYMMDD } from '@/shared/utils/date';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DEFAULT_HOURS_SLIDER_VALUE,
  DEFAULT_MINIMUM_WINDOW,
  TIMEFRAME_OPTIONS,
  TIMING_OPTIONS,
} from '../constants';
import TimeRangeSlider from '../TimeRangeSlider/TimeRangeSlider';
import TimezoneSelect from '../TimezoneSelect/TimezoneSelect';
import {
  CanvasserScheduleFormData,
  CanvasserScheduleValidationErrors,
  RequestCanvassersFormProps,
} from '../types';

const RequestCanvassersForm = ({
  jobId,
  onSubmit,
  onCancel,
  isLoading: externalIsLoading = false,
}: RequestCanvassersFormProps) => {
  const accountDetails = useSelector(selectAccountDetails);
  const { data: jobDetails } = useJobDetails(jobId);
  const { submitCanvasserRequest, isSubmitting } = useCanvassers(jobId);
  const { validateInitials, expectedInitials } = useInitialsValidation();

  // Get minimum window from account settings or use default
  const minimumWindow =
    accountDetails?.initial_canvasser_schedule_minimum_window || DEFAULT_MINIMUM_WINDOW;

  // Form state
  const [formData, setFormData] = useState<CanvasserScheduleFormData>({
    timing: 'asap',
    requestedDate: '',
    timeframe: 'anytime',
    timezone: 'LOCAL',
    startTime: DEFAULT_HOURS_SLIDER_VALUE[0],
    endTime: DEFAULT_HOURS_SLIDER_VALUE[1],
    termsAccepted: false,
    userInitials: '',
  });

  const [validationErrors, setValidationErrors] =
    useState<CanvasserScheduleValidationErrors>({});

  // Dynamic form behavior
  const isAsapSelected = formData.timing === 'asap';
  const shouldShowDatePicker = !isAsapSelected;
  const shouldShowTimeframe = !isAsapSelected;
  const shouldShowTimezone = !isAsapSelected;
  const isSliderDisabled = isAsapSelected || formData.timeframe === 'anytime';

  // Calculate date display for slider
  const getDateDisplay = () => {
    if (isAsapSelected) {
      return 'Today';
    }

    if (!formData.requestedDate) {
      return undefined;
    }

    // Parse date manually to avoid timezone issues
    const [year, month, day] = formData.requestedDate.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day); // month is 0-indexed

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // Normalize all dates to midnight for comparison
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);

    if (selectedDate.getTime() === today.getTime()) {
      return 'Today';
    }

    if (selectedDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    }

    // Format as mm/dd/yy using the parsed values directly
    const monthFormatted = month.toString().padStart(2, '0');
    const dayFormatted = day.toString().padStart(2, '0');
    const yearFormatted = year.toString().slice(-2);
    return `${monthFormatted}/${dayFormatted}/${yearFormatted}`;
  };

  // Get minimum date (today or job start date, whichever is later)
  const minimumDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight for proper comparison

    const jobStartDate = jobDetails?.start_date ? new Date(jobDetails.start_date) : today;
    jobStartDate.setHours(0, 0, 0, 0); // Set to midnight for proper comparison

    return jobStartDate > today ? jobStartDate : today;
  }, [jobDetails?.start_date]);

  const handleTimingChange = useCallback((timing: string) => {
    const isAsap = timing === 'asap';
    const todayFormatted = getFormattedDateYYYYMMDD(new Date());

    setFormData((prev) => ({
      ...prev,
      timing: timing as 'asap' | 'future',
      requestedDate: isAsap ? todayFormatted : prev.requestedDate,
      timeframe: isAsap ? 'anytime' : prev.timeframe,
      timezone: isAsap ? 'LOCAL' : prev.timezone,
      startTime: isAsap ? DEFAULT_HOURS_SLIDER_VALUE[0] : prev.startTime,
      endTime: isAsap ? DEFAULT_HOURS_SLIDER_VALUE[1] : prev.endTime,
    }));
    setValidationErrors((prev) => ({ ...prev, requestedDate: undefined }));
  }, []);

  const handleTimeframeChange = useCallback((timeframe: string) => {
    setFormData((prev) => ({
      ...prev,
      timeframe: timeframe as 'anytime' | 'custom',
      timezone: timeframe === 'anytime' ? 'LOCAL' : 'US/Eastern',
      startTime: timeframe === 'anytime' ? DEFAULT_HOURS_SLIDER_VALUE[0] : prev.startTime,
      endTime: timeframe === 'anytime' ? DEFAULT_HOURS_SLIDER_VALUE[1] : prev.endTime,
    }));
  }, []);

  const handleDateChange = useCallback(
    (selectedDate: string) => {
      setFormData((prev) => ({ ...prev, requestedDate: selectedDate }));

      // Validate date
      if (selectedDate) {
        // Create date in local timezone to match minimumDate calculation
        const [year, month, day] = selectedDate.split('-').map(Number);
        const selected = new Date(year, month - 1, day); // month is 0-indexed

        if (selected < minimumDate) {
          // Format date as mm/dd/yy to match input format
          const minMonth = (minimumDate.getMonth() + 1).toString().padStart(2, '0');
          const minDay = minimumDate.getDate().toString().padStart(2, '0');
          const minYear = minimumDate.getFullYear().toString().slice(-2);
          const formattedMinDate = `${minMonth}/${minDay}/${minYear}`;

          setValidationErrors((prev) => ({
            ...prev,
            requestedDate: `Date cannot be before ${formattedMinDate}`,
          }));
        } else {
          setValidationErrors((prev) => ({ ...prev, requestedDate: undefined }));
        }
      }
    },
    [minimumDate]
  );

  const handleTimeRangeChange = useCallback((startTime: number, endTime: number) => {
    setFormData((prev) => ({ ...prev, startTime, endTime }));
  }, []);

  const handleTimezoneChange = useCallback((timezone: string) => {
    setFormData((prev) => ({ ...prev, timezone }));
  }, []);

  const handleInitialsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const initials = e.target.value.toUpperCase();
      setFormData((prev) => ({ ...prev, userInitials: initials }));

      // Validate initials
      const validation = validateInitials(initials);
      setValidationErrors((prev) => ({
        ...prev,
        userInitials: validation.isValid ? undefined : validation.error,
      }));
    },
    [validateInitials]
  );

  const handleTermsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const accepted = e.target.checked;
    setFormData((prev) => ({ ...prev, termsAccepted: accepted }));
    setValidationErrors((prev) => ({
      ...prev,
      termsAccepted: accepted
        ? undefined
        : 'You must accept the terms and conditions to proceed.',
    }));
  }, []);

  // Form validation
  const isFormValid = useMemo(() => {
    const hasNoErrors = Object.values(validationErrors).every((error) => !error);
    const hasRequiredFields = formData.termsAccepted && formData.userInitials.length > 0;
    const hasValidDate = isAsapSelected || formData.requestedDate;

    return hasNoErrors && hasRequiredFields && hasValidDate;
  }, [validationErrors, formData, isAsapSelected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    try {
      await submitCanvasserRequest(formData);
      onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      setValidationErrors((prev) => ({
        ...prev,
        general: 'An error occurred while submitting your request. Please try again.',
      }));
    }
  };

  const isLoading = isSubmitting || externalIsLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error */}
      {validationErrors.general && (
        <div className="alert alert-error">
          <span>{validationErrors.general}</span>
        </div>
      )}

      {/* Timing Selection */}
      <div className="form-control">
        <label className="label">
          <span className="label-text">When do you need to schedule canvassers?</span>
        </label>
        <div>
          {TIMING_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="label cursor-pointer justify-start gap-3 py-1">
              <input
                type="radio"
                name="timing"
                value={option.value}
                checked={formData.timing === option.value}
                onChange={(e) => handleTimingChange(e.target.value)}
                className="radio-primary radio"
                disabled={isLoading}
              />
              <span className="label-text">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Date Selection */}
      {shouldShowDatePicker && (
        <DatePicker
          label="Date"
          value={formData.requestedDate}
          onChange={handleDateChange}
          min={getFormattedDateYYYYMMDD(minimumDate)}
          error={validationErrors.requestedDate}
          disabled={isLoading}
          required
        />
      )}

      {/* Timeframe Selection */}
      {shouldShowTimeframe && (
        <div className="form-control">
          <label className="label">
            <span className="label-text">
              What time frame do you need to schedule canvassers?
            </span>
          </label>
          <div>
            {TIMEFRAME_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="label cursor-pointer justify-start gap-3 py-1">
                <input
                  type="radio"
                  name="timeframe"
                  value={option.value}
                  checked={formData.timeframe === option.value}
                  onChange={(e) => handleTimeframeChange(e.target.value)}
                  className="radio-primary radio"
                  disabled={isLoading}
                />
                <span className="label-text">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Time Range Slider - Show for both ASAP (disabled) and future (enabled/disabled based on timeframe) */}
      <div className="form-control">
        <TimeRangeSlider
          startTime={formData.startTime}
          endTime={formData.endTime}
          onChange={handleTimeRangeChange}
          disabled={isSliderDisabled || isLoading}
          minimumWindow={minimumWindow}
          dateDisplay={getDateDisplay()}
        />
      </div>

      {/* Timezone Selection */}
      {shouldShowTimezone && (
        <TimezoneSelect
          value={formData.timezone}
          onChange={handleTimezoneChange}
          disabled={isLoading || formData.timeframe === 'anytime'}
        />
      )}

      {/* Terms and Conditions - Moved above initials with top alignment */}
      <div className="form-control">
        <label className="label cursor-pointer items-start justify-start gap-3">
          <input
            type="checkbox"
            checked={formData.termsAccepted}
            onChange={handleTermsChange}
            className={`checkbox-primary checkbox mt-1 ${validationErrors.termsAccepted ? 'checkbox-error' : ''}`}
            disabled={isLoading}
            required
          />
          <span className="label-text text-sm text-wrap">
            By checking this box you confirm, certify and represent that you, in your sole
            discretion: (i) selected the recipient numbers to be contacted; (ii) created
            the contact list data; (iii) authorized and directed Canvasser Agents to
            initiate the transmission of all communications sent on your Account; and (iv)
            chose the time and date for the transmission of all messages. You further
            confirm, certify and represent that Peerly Inc. and any Canvassing Agents have
            and had no role whatsoever in the: (i) creation of the message content; (ii)
            creation of the contact list; (iii) choosing of the time for the transmission
            of the messages; and/or; (iv) decision to initiate the transmission of the
            messages. You confirm, certify and represent that the Canvasser Agents are
            acting solely on your authorization, instructions and direction as your
            sub-agents.
          </span>
        </label>
        {validationErrors.termsAccepted && (
          <label className="label">
            <span className="text-error label-text-alt">
              {validationErrors.termsAccepted}
            </span>
          </label>
        )}
      </div>

      {/* User Initials - Moved below terms, made smaller */}
      <div className="form-control">
        <label className="label">
          <span className="label-text text-sm text-wrap">
            Please enter your initials to confirm that you acknowledge and agree to the
            above statements.
          </span>
        </label>
        <input
          type="text"
          value={formData.userInitials}
          onChange={handleInitialsChange}
          placeholder={expectedInitials}
          maxLength={3}
          className={`input input-bordered w-20 ${validationErrors.userInitials ? 'input-error' : ''}`}
          disabled={isLoading}
          required
        />
        {validationErrors.userInitials && (
          <label className="label">
            <span className="text-error label-text-alt">
              {validationErrors.userInitials}
            </span>
          </label>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
          disabled={isLoading}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!isFormValid || isLoading}>
          {isLoading ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Submitting...
            </>
          ) : (
            'Submit & Initiate'
          )}
        </button>
      </div>
    </form>
  );
};

export default RequestCanvassersForm;
